import { useContext, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useHistory } from 'react-router-dom';
import Banner from '../Components/Banner/Banner';
import CategoryGrid from '../Components/Home/CategoryGrid';
import LocationBanner from '../Components/Home/LocationBanner';
import QuickMenu from '../Components/Home/QuickMenu';
import Layout from '../Components/Layout/Layout';
import NearbyAds from '../Components/Location/NearbyAds';
import Posts from '../Components/Posts/Posts';
import { LocationContext } from '../contextStore/LocationContext';
import { Firebase } from '../firebase/config';
import { seededShuffle } from '../utils/seededShuffle';
import './Home.css';

const QUICK_MENU_COUNT = 8;

/**
 * Sort products by admin-assigned marketingScore (desc) then by createdAt (desc).
 * Products with a higher marketingScore appear first in the Quick Menu.
 * Products without a score default to 0 and fall back to chronological order.
 * The marketingScore field (0–100) is set by admins via Firebase Console.
 */
function sortByMarketingScore(list) {
  return [...list].sort((a, b) => {
    const scoreA = Number(a.marketingScore) || 0;
    const scoreB = Number(b.marketingScore) || 0;
    if (scoreA !== scoreB) return scoreB - scoreA;
    // Same score — newest first
    const dateA = a.createdAt?.toDate
      ? a.createdAt.toDate()
      : new Date(a.createdAt || 0);
    const dateB = b.createdAt?.toDate
      ? b.createdAt.toDate()
      : new Date(b.createdAt || 0);
    return dateB - dateA;
  });
}

function Home() {
  const { browseLocation } = useContext(LocationContext);
  const history = useHistory();
  const [visitSeed] = useState(() => Date.now());
  const [allProducts, setAllProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);

  useEffect(() => {
    setProductsLoading(true);
    Firebase.firestore()
      .collection('products')
      .where('status', '==', 'active')
      .orderBy('createdAt', 'desc')
      .get()
      .then((snapshot) => {
        const list = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
        }));
        setAllProducts(list);
      })
      .catch((err) => {
        console.error('--- Home query ERROR ---', err);
        setAllProducts([]);
      })
      .finally(() => setProductsLoading(false));
  }, []);

  // Smart filtering logic:
  // 1. If city is selected: Try City, then State, then Global.
  // 2. If only state is selected: Try State, then Global.
  // 3. Otherwise: Global.
  const filteredProducts = useMemo(() => {
    if (!allProducts.length) return [];

    const city = browseLocation?.city?.toLowerCase();
    const state = browseLocation?.state?.toLowerCase();

    if (!city && !state) return allProducts;

    let cityMatches = [];
    let stateMatches = [];

    if (city || state) {
      allProducts.forEach((p) => {
        const pCity = (p.location?.city || '').toLowerCase();
        const pState = (p.location?.state || '').toLowerCase();

        if (city && pCity === city) {
          cityMatches.push(p);
        } else if (state && pState === state) {
          stateMatches.push(p);
        }
      });
    }

    // Determine the primary list based on availability
    if (city && cityMatches.length > 0) return cityMatches;
    if (state && stateMatches.length > 0) return stateMatches;

    return allProducts; // Fallback
  }, [allProducts, browseLocation]);

  // Quick Menu: top candidates by marketingScore, then time-seeded shuffle for variety per visit
  const sortedByScore = useMemo(
    () => sortByMarketingScore(filteredProducts),
    [filteredProducts]
  );

  const quickMenuProducts = useMemo(() => {
    return seededShuffle(
      sortedByScore.slice(0, QUICK_MENU_COUNT * 2),
      visitSeed
    ).slice(0, QUICK_MENU_COUNT);
  }, [sortedByScore, visitSeed]);

  // Fresh Recommendations: time-seeded shuffle so order varies per visit
  const freshProducts = useMemo(
    () => seededShuffle(filteredProducts, visitSeed),
    [filteredProducts, visitSeed]
  );

  const homeJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'DealSpotNG',
    alternateName: [
      'Deal Spot',
      'Deal Spot NG',
      'DealSpot NG',
      'DealSpot Nigeria',
    ],
    url: 'https://dealspotng.dabaras.com/',
    description:
      "Nigeria's leading online classifieds and listings marketplace. Buy and sell cars, phones, electronics, fashion, real estate, jobs and services.",
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate:
          'https://dealspotng.dabaras.com/search?q={search_term_string}',
      },
      'query-input': 'required name=search_term_string',
    },
  };

  const itemListJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Featured Listings on DealSpotNG',
    description: 'Browse the latest classified ads and listings on DealSpotNG',
    numberOfItems: filteredProducts.length,
    itemListElement: filteredProducts.slice(0, 10).map((product, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: product.name || 'Listing',
      url: `https://dealspotng.dabaras.com/ad/${product.id}`,
    })),
  };

  return (
    <Layout>
      <Helmet>
        <title>
          DealSpotNG - Buy &amp; Sell Anything in Nigeria | Free Classifieds
          &amp; Listings Marketplace
        </title>
        <meta
          name="description"
          content="DealSpotNG - Nigeria's leading online classifieds and listings marketplace. Buy and sell cars, phones, electronics, fashion, real estate, jobs and services. Post free ads and find the best deals near you."
        />
        <meta
          name="keywords"
          content="DealSpotNG, Deal Spot, Deal Spot NG, classifieds, classified ads, listings, marketplace, buy and sell, Nigeria, online marketplace, free ads, post ads, cars for sale, phones for sale, electronics, fashion, real estate, jobs, services, used items, second hand, Lagos, Abuja, Port Harcourt, deals, best deals, cheap, affordable, sell online, buy online, Nigerian marketplace, classified website, listing website"
        />
        <link rel="canonical" href="https://dealspotng.dabaras.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://dealspotng.dabaras.com/" />
        <meta
          property="og:title"
          content="DealSpotNG - Buy & Sell Anything in Nigeria | Free Classifieds & Listings"
        />
        <meta
          property="og:description"
          content="Nigeria's leading online classifieds and listings marketplace. Buy and sell cars, phones, electronics, fashion, real estate, jobs and services. Post free ads and find the best deals near you."
        />
        <meta
          property="og:image"
          content="https://dealspotng.dabaras.com/assets/images/dealspotngicon.png"
        />
        <meta property="og:site_name" content="DealSpotNG" />
        <meta property="og:locale" content="en_NG" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="DealSpotNG - Buy & Sell Anything in Nigeria | Free Classifieds & Listings"
        />
        <meta
          name="twitter:description"
          content="Nigeria's leading online classifieds and listings marketplace. Buy and sell cars, phones, electronics, fashion, real estate, jobs and services."
        />
        <meta
          name="twitter:image"
          content="https://dealspotng.dabaras.com/assets/images/dealspotngicon.png"
        />
        <script type="application/ld+json">{JSON.stringify(homeJsonLd)}</script>
        <script type="application/ld+json">
          {JSON.stringify(itemListJsonLd)}
        </script>
      </Helmet>
      <div className="homeParentDiv">
        <LocationBanner />
        <CategoryGrid
          onSelectCategory={(name, id) =>
            history.push(
              id ? `/category/${id}` : `/search?q=${encodeURIComponent(name)}`
            )
          }
        />
        <Banner />
        <QuickMenu products={quickMenuProducts} loading={productsLoading} />
        <Posts
          allPosts={allProducts}
          freshPosts={freshProducts}
          loading={productsLoading}
        />
        {browseLocation?.city && (
          <NearbyAds
            city={browseLocation.city}
            state={browseLocation.state}
            limit={8}
          />
        )}
      </div>
    </Layout>
  );
}

export default Home;
